import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Camera, Sparkles, Palette, Zap, CheckCircle, ArrowRight, Home as HomeIcon, Box, User, Package, FileText, Phone, Mail, Clock, Award, Target, Image, ShoppingBag, Hourglass, ChevronLeft, ChevronRight, Star, X, Film } from 'lucide-react';
import { useEditMode } from './EditModeContext';
import Navigation from './Navigation';
import Footer from './Footer';
import EditToolbar from './components/EditToolbar';
import EditableText from './components/EditableText';
import EditableMedia from './components/EditableMedia';
import MediaPicker from './components/MediaPicker';
import api from './services/cloudflare-api';

export default function Home() {
  const [searchParams] = useSearchParams();
  const { isEditMode, enableEditMode, updateContent, getContent } = useEditMode();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [currentFeatureGallery, setCurrentFeatureGallery] = useState(0);
  const [showHeroMediaManager, setShowHeroMediaManager] = useState(false);
  const [currentEditingSlideId, setCurrentEditingSlideId] = useState(null);
  const [promoBarText, setPromoBarText] = useState('20% off on your first order (Code "FIRSTORDER"). Sign up and get free trial');
  const slideGalleryRef = useRef(null);
  const testimonialScrollRef = useRef(null);
  const scrollAnimationRef = useRef(null);

  // Check for edit mode parameter
  useEffect(() => {
    if (searchParams.get('edit') === 'true') {
      enableEditMode('home');
    }
  }, [searchParams, enableEditMode]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load PromoBar text from D1
  useEffect(() => {
    const loadPromoBar = async () => {
      try {
        const config = await api.getSiteConfig();
        if (config.promo_bar && config.promo_bar !== 'null' && config.promo_bar_active === 1) {
          setPromoBarText(config.promo_bar);
        } else {
          // Hide PromoBar if inactive
          setPromoBarText('');
        }
      } catch (error) {
        console.error('Error loading promo bar from D1:', error);
        // Fallback to localStorage if D1 fails
        const savedPromoText = localStorage.getItem('global_promobar_text');
        if (savedPromoText) {
          setPromoBarText(savedPromoText);
        }
      }
    };

    loadPromoBar();

    // Listen for updates from PromoBarEditor
    const handlePromoBarUpdate = () => {
      loadPromoBar();
    };

    window.addEventListener('promoBarUpdated', handlePromoBarUpdate);
    return () => window.removeEventListener('promoBarUpdated', handlePromoBarUpdate);
  }, []);

  // Infinite scroll logic for testimonials
  useEffect(() => {
    const scrollContainer = testimonialScrollRef.current;
    if (!scrollContainer) return;

    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame
    let isPaused = false;

    const scroll = () => {
      if (!isPaused && scrollContainer) {
        scrollPosition += scrollSpeed;

        // Get the width of one testimonial card (320px) + gap (24px) = 344px
        const cardWidth = 344;
        const totalWidth = cardWidth * 9; // 9 testimonials

        // When we've scrolled past one full set, reset to 0
        if (scrollPosition >= totalWidth) {
          scrollPosition = 0;
        }

        scrollContainer.style.transform = `translateX(-${scrollPosition}px)`;
      }

      scrollAnimationRef.current = requestAnimationFrame(scroll);
    };

    // Mouse enter/leave handlers for pause on hover
    const handleMouseEnter = () => {
      isPaused = true;
    };

    const handleMouseLeave = () => {
      isPaused = false;
    };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    scrollAnimationRef.current = requestAnimationFrame(scroll);

    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Default hero slides
  const defaultHeroSlides = [
    {
      id: 'hero_slide_1',
      title: 'Real Estate & Architectural Images',
      description: 'Professional photo editing services essential for selling properties faster',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80'
    },
    {
      id: 'hero_slide_2',
      title: 'Banner & Album Design',
      description: 'Creative designs that capture attention and tell your story',
      image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1920&q=80'
    },
    {
      id: 'hero_slide_3',
      title: 'Drafting & 3D Rendering',
      description: 'Transform architectural visions into photorealistic reality',
      image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1920&q=80'
    },
    {
      id: 'hero_slide_4',
      title: 'People & Product Retouching',
      description: 'Transform ordinary images into extraordinary visual assets',
      image: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=1920&q=80'
    },
    {
      id: 'hero_slide_5',
      title: 'Wedding Retouch',
      description: 'Perfect memories deserve perfect photos',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=80'
    }
  ];

  // State for hero slides
  const [heroSlides, setHeroSlides] = useState(defaultHeroSlides);
  const [loadingSlides, setLoadingSlides] = useState(true);
  const [apiError, setApiError] = useState(null);

  // Load hero slides from D1 API (with localStorage fallback)
  useEffect(() => {
    async function loadHeroSlides() {
      try {
        setLoadingSlides(true);
        setApiError(null);

        // Load ONLY from D1 API - no localStorage fallback
        console.log('📡 Loading hero slides from D1 API...');
        const apiSlides = await api.getHeroSlides();

        if (apiSlides && apiSlides.length > 0) {
          // Transform API data to match component format
          const transformedSlides = apiSlides.map((slide) => ({
            id: slide.id,
            title: slide.title,
            description: slide.description || '',
            image: slide.media_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80',
            video: slide.media_type === 'video' ? slide.media_url : null,
            media_key: slide.media_key || '',
            media_type: slide.media_type || 'image',
            order_num: slide.order_num || 0
          }));

          // Sort by order_num
          transformedSlides.sort((a, b) => a.order_num - b.order_num);

          setHeroSlides(transformedSlides);
          console.log('✅ Loaded', transformedSlides.length, 'hero slides from D1 API');
        } else {
          console.warn('⚠️ No slides found in D1 database');
          setHeroSlides([]);
        }
      } catch (error) {
        console.error('❌ Failed to load from D1 API:', error);
        setApiError(error.message);
        setHeroSlides([]); // Show empty instead of localStorage data
      } finally {
        setLoadingSlides(false);
      }
    }

    loadHeroSlides();
  }, []);

  // Listen for hero slide updates (title, description, images) and sync to D1
  useEffect(() => {
    const handleContentUpdate = async (event) => {
      console.log('🔔 Full event detail:', event.detail);
      const { key, page } = event.detail || {};
      console.log('🔔 Content updated event - key:', key, 'page:', page);

      // Handle individual element updates (blur)
      if (key && key.startsWith('home_hero_slide_')) {
        console.log('📝 Individual hero slide update detected:', key);

        // Extract slide ID from key
        const slideIdMatch = key.match(/home_hero_slide_(\d+)_/);
        if (slideIdMatch) {
          const slideId = parseInt(slideIdMatch[1]);
          const slide = heroSlides.find(s => s.id === slideId);

          if (slide) {
            // Get the edited value from localStorage (what user just typed)
            const savedImage = getContent(`home_hero_slide_${slide.id}_image`, slide.image);
            const savedTitle = getContent(`home_hero_slide_${slide.id}_title`, slide.title);
            const savedDesc = getContent(`home_hero_slide_${slide.id}_description`, slide.description);

            // Sync to D1 API immediately
            try {
              await api.updateHeroSlide(slideId, {
                title: savedTitle,
                description: savedDesc,
                media_url: savedImage,
                media_key: slide.media_key || '',
                media_type: slide.media_type || 'image',
                order_num: slide.order_num || 0
              });
              console.log('✅ Synced slide to D1:', slideId);

              // Reload from D1 to ensure consistency
              const updatedSlides = await api.getHeroSlides();
              const transformedSlides = updatedSlides.map((s) => ({
                id: s.id,
                title: s.title,
                description: s.description || '',
                image: s.media_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80',
                video: s.media_type === 'video' ? s.media_url : null,
                media_key: s.media_key || '',
                media_type: s.media_type || 'image',
                order_num: s.order_num || 0
              }));
              transformedSlides.sort((a, b) => a.order_num - b.order_num);
              setHeroSlides(transformedSlides);

              // Clear localStorage for home page content since D1 is now the source of truth
              localStorage.removeItem('homePageContent');
              console.log('🗑️ Cleared localStorage - D1 is now source of truth');
            } catch (error) {
              console.error('❌ Failed to sync slide to D1:', error);
            }
          }
        }
      } else if (page === 'home' && !key) {
        // Handle "Save Changes" button click (no specific key)
        // Sync ALL hero slides to D1
        console.log('💾 Bulk save detected - syncing all hero slides to D1');
        console.log('📊 Current heroSlides count:', heroSlides.length);

        try {
          for (const slide of heroSlides) {
            console.log('🔄 Processing slide ID:', slide.id);
            const savedTitle = getContent(`home_hero_slide_${slide.id}_title`, slide.title);
            const savedDesc = getContent(`home_hero_slide_${slide.id}_description`, slide.description);
            const savedImage = getContent(`home_hero_slide_${slide.id}_image`, slide.image);

            console.log('📝 Syncing slide', slide.id, '- Title:', savedTitle);

            await api.updateHeroSlide(slide.id, {
              title: savedTitle,
              description: savedDesc,
              media_url: savedImage,
              media_key: slide.media_key || '',
              media_type: slide.media_type || 'image',
              order_num: slide.order_num || 0
            });
            console.log('✅ Synced slide to D1:', slide.id);
          }
          console.log('✅ All slides synced to D1');

          // Reload from D1 to ensure consistency across all browsers
          const updatedSlides = await api.getHeroSlides();
          const transformedSlides = updatedSlides.map((s) => ({
            id: s.id,
            title: s.title,
            description: s.description || '',
            image: s.media_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80',
            video: s.media_type === 'video' ? s.media_url : null,
            media_key: s.media_key || '',
            media_type: s.media_type || 'image',
            order_num: s.order_num || 0
          }));
          transformedSlides.sort((a, b) => a.order_num - b.order_num);
          setHeroSlides(transformedSlides);
          console.log('🔄 Reloaded all slides from D1');

          // Clear localStorage for home page content since D1 is now the source of truth
          localStorage.removeItem('homePageContent');
          console.log('🗑️ Cleared localStorage - D1 is now source of truth');
        } catch (error) {
          console.error('❌ Error in bulk sync:', error);
        }
      }
    };

    window.addEventListener('contentUpdated', handleContentUpdate);
    return () => window.removeEventListener('contentUpdated', handleContentUpdate);
  }, [heroSlides, getContent]);

  useEffect(() => {
    // Don't auto-advance slides in edit mode
    if (isEditMode) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isEditMode, heroSlides.length]);

  // Add new hero slide (D1 API)
  const handleAddHeroSlide = async () => {
    try {
      const newSlideData = {
        title: 'New Slide Title',
        description: 'New slide description',
        media_url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80',
        media_type: 'image',
        order_num: heroSlides.length + 1
      };

      const result = await api.createHeroSlide(newSlideData);
      console.log('✅ Created new slide with ID:', result.id);

      // Reload slides from API
      const updatedSlides = await api.getHeroSlides();
      const transformedSlides = updatedSlides.map((slide) => ({
        id: slide.id,
        title: slide.title,
        description: slide.description || '',
        image: slide.media_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80',
        video: slide.media_type === 'video' ? slide.media_url : null,
        media_key: slide.media_key || '',
        media_type: slide.media_type || 'image',
        order_num: slide.order_num || 0
      }));
      transformedSlides.sort((a, b) => a.order_num - b.order_num);
      setHeroSlides(transformedSlides);

      // Navigate to the new slide
      setCurrentSlide(transformedSlides.length - 1);
    } catch (error) {
      console.error('❌ Failed to create slide:', error);
      alert('Failed to create slide. Please try again.');
    }
  };

  // Delete hero slide (D1 API)
  const handleDeleteHeroSlide = async (slideId) => {
    if (heroSlides.length <= 1) {
      alert('You must have at least one hero slide!');
      return;
    }
    if (window.confirm('Delete this hero slide?')) {
      try {
        await api.deleteHeroSlide(slideId);
        console.log('✅ Deleted slide ID:', slideId);

        // Reload slides from API
        const updatedSlides = await api.getHeroSlides();
        const transformedSlides = updatedSlides.map((slide) => ({
          id: slide.id,
          title: slide.title,
          description: slide.description || '',
          image: slide.media_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80',
          video: slide.media_type === 'video' ? slide.media_url : null,
          media_key: slide.media_key || '',
          media_type: slide.media_type || 'image',
          order_num: slide.order_num || 0
        }));
        transformedSlides.sort((a, b) => a.order_num - b.order_num);
        setHeroSlides(transformedSlides);

        // Adjust current slide index if needed
        if (currentSlide >= transformedSlides.length) {
          setCurrentSlide(transformedSlides.length - 1);
        }
      } catch (error) {
        console.error('❌ Failed to delete slide:', error);
        alert('Failed to delete slide. Please try again.');
      }
    }
  };

  // Helper to detect if URL is a video
  const isVideoUrl = (url) => {
    if (!url) return false;
    if (url.startsWith('data:video/')) return true;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const urlWithoutParams = url.split('?')[0].split('#')[0];
    return videoExtensions.some(ext => urlWithoutParams.toLowerCase().endsWith(ext));
  };

  // Default gallery items
  const defaultFeatureGalleryItems = [
    {
      id: 'gallery_1',
      title: "2x Telephoto.",
      description: "Close in or zoom out on your subjects with exceptional detail.",
      image: "https://images.unsplash.com/photo-1606229365485-93a3b8ee0385?w=800&q=80",
      size: "large"
    },
    {
      id: 'gallery_2',
      title: "Clean Up.",
      description: "Remove unwanted objects, people, and background distractions from your photos.",
      image: "https://images.unsplash.com/photo-1551522435-a13afa10f103?w=800&q=80",
      size: "small"
    },
    {
      id: 'gallery_3',
      title: "Low-light and Night.",
      description: "Capture more detailed, and reduced noise, even in challenging conditions.",
      image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800&q=80",
      size: "large"
    },
    {
      id: 'gallery_4',
      title: "Portrait Mode.",
      description: "Professional depth effects with studio-quality lighting control.",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&q=80",
      size: "small"
    },
    {
      id: 'gallery_5',
      title: "4K Video.",
      description: "Cinematic video recording with advanced stabilization and color accuracy.",
      image: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&q=80",
      size: "large"
    }
  ];

  // State for feature gallery items
  const [featureGalleryItems, setFeatureGalleryItems] = useState(defaultFeatureGalleryItems);

  // Load gallery items from D1 database
  useEffect(() => {
    async function loadGalleryFromD1() {
      try {
        const items = await api.getFeatureGalleryItems();
        const transformedItems = items.map((item) => ({
          id: item.id,
          title: item.title,
          description: item.description || '',
          image: item.image_url,
          size: item.size || 'large'
        }));
        setFeatureGalleryItems(transformedItems);
        console.log('✅ Loaded feature gallery from D1:', transformedItems.length, 'items');
      } catch (error) {
        console.error('❌ Failed to load gallery from D1:', error);
        // Fallback to localStorage if D1 fails
        const savedGallery = getContent('home_slidegallery_items_data', null);
        if (savedGallery) {
          try {
            const parsedGallery = JSON.parse(savedGallery);
            setFeatureGalleryItems(parsedGallery);
            console.log('⚠️ Using localStorage fallback for gallery');
          } catch (e) {
            console.error('Error parsing saved gallery:', e);
            setFeatureGalleryItems(defaultFeatureGalleryItems);
          }
        } else {
          setFeatureGalleryItems(defaultFeatureGalleryItems);
        }
      }
    }

    loadGalleryFromD1();
  }, [getContent]);

  // Listen for gallery item image updates and sync to D1
  useEffect(() => {
    const handleImageUpdate = async (event) => {
      // Only respond to individual gallery item changes
      const { key } = event.detail || {};
      if (key && key.startsWith('home_slidegalary_item_')) {
        // Extract gallery item ID from key like "home_slidegalary_item_gallery_1"
        const itemId = key.replace('home_slidegalary_item_', '');
        const savedImage = getContent(key, null);

        if (savedImage) {
          console.log(`📸 Gallery image updated for ${itemId}:`, savedImage);

          // Update local state immediately
          setFeatureGalleryItems((currentGallery) => {
            return currentGallery.map((item) => {
              if (item.id === itemId) {
                return { ...item, image: savedImage };
              }
              return item;
            });
          });

          // Sync to D1 database
          try {
            await api.updateFeatureGalleryItem({
              id: itemId,
              image_url: savedImage
            });
            console.log('✅ Synced gallery item to D1:', itemId);
          } catch (error) {
            console.error('❌ Failed to sync gallery item to D1:', error);
          }
        }
      }
    };

    window.addEventListener('contentUpdated', handleImageUpdate);
    return () => window.removeEventListener('contentUpdated', handleImageUpdate);
  }, [getContent]);

  // Toggle gallery item size and sync to D1
  const handleToggleSize = async (itemId) => {
    const item = featureGalleryItems.find(i => i.id === itemId);
    const newSize = item.size === 'large' ? 'small' : 'large';

    // Update local state immediately
    const updatedGallery = featureGalleryItems.map((item) =>
      item.id === itemId ? { ...item, size: newSize } : item
    );
    setFeatureGalleryItems(updatedGallery);

    // Sync to D1
    try {
      await api.updateFeatureGalleryItem({
        id: itemId,
        size: newSize
      });
      console.log('✅ Synced size change to D1:', itemId);
    } catch (error) {
      console.error('❌ Failed to sync size to D1:', error);
    }
  };

  // Add new gallery item and sync to D1
  const handleAddGalleryItem = async () => {
    const newId = `gallery_${Date.now()}`;
    const newItem = {
      id: newId,
      title: "New Item",
      description: "Description",
      image_url: "https://images.unsplash.com/photo-1606229365485-93a3b8ee0385?w=800&q=80",
      size: "large",
      order_num: featureGalleryItems.length + 1
    };

    // Add to local state
    setFeatureGalleryItems([...featureGalleryItems, {
      id: newId,
      title: newItem.title,
      description: newItem.description,
      image: newItem.image_url,
      size: newItem.size
    }]);

    // Sync to D1
    try {
      await api.createFeatureGalleryItem(newItem);
      console.log('✅ Created gallery item in D1:', newId);
    } catch (error) {
      console.error('❌ Failed to create gallery item in D1:', error);
    }
  };

  // Delete gallery item and sync to D1
  const handleDeleteGalleryItem = async (itemId) => {
    if (featureGalleryItems.length <= 1) {
      alert('Cannot delete the last gallery item!');
      return;
    }
    if (window.confirm('Delete this gallery item?')) {
      // Remove from local state
      const updatedGallery = featureGalleryItems.filter(item => item.id !== itemId);
      setFeatureGalleryItems(updatedGallery);

      // Sync to D1
      try {
        await api.deleteFeatureGalleryItem(itemId);
        console.log('✅ Deleted gallery item from D1:', itemId);
      } catch (error) {
        console.error('❌ Failed to delete gallery item from D1:', error);
      }
    }
  };

  const navigateSlideGallery = (direction) => {
    if (slideGalleryRef.current) {
      const scrollAmount = direction === 'left' ? -720 : 720;
      slideGalleryRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }

    if (direction === 'left') {
      setCurrentFeatureGallery((prev) => (prev - 1 + featureGalleryItems.length) % featureGalleryItems.length);
    } else {
      setCurrentFeatureGallery((prev) => (prev + 1) % featureGalleryItems.length);
    }
  };

  // Default services list
  const defaultServicesList = [
    "Skin Retouching & Beauty Enhancement",
    "Advanced Color Grading",
    "Background Manipulation",
    "Light & Shadow Refinement",
    "Perspective & Distortion Correction",
    "Batch Processing Services"
  ];

  // State for services list
  const [servicesList, setServicesList] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);

  // Load home service lists from D1 API (only)
  useEffect(() => {
    async function loadHomeServiceLists() {
      try {
        setLoadingServices(true);
        console.log('📡 Loading home service lists from D1 API...');

        const apiServices = await api.getHomeServiceLists();

        if (apiServices && apiServices.length > 0) {
          // Sort by order_num
          apiServices.sort((a, b) => a.order_num - b.order_num);

          setServicesList(apiServices);
          console.log('✅ Loaded', apiServices.length, 'home service lists from D1 API');
        } else {
          console.warn('⚠️ No home service lists found in D1 database');
          setServicesList([]);
        }
      } catch (error) {
        console.error('❌ Failed to load home service lists from D1 API:', error);
        setServicesList([]);
      } finally {
        setLoadingServices(false);
      }
    }

    loadHomeServiceLists();
  }, []);

  // Listen for service name edits and sync to D1
  useEffect(() => {
    const handleServiceUpdate = async (event) => {
      const { key, page } = event.detail || {};

      // Only handle service list updates
      if (page === 'home' && key && key.startsWith('home_servicelist_item_')) {
        console.log('📝 Service name edit detected:', key);

        // Extract service ID from key (e.g., home_servicelist_item_5)
        const serviceIdMatch = key.match(/home_servicelist_item_(\d+)/);
        if (serviceIdMatch) {
          const serviceId = parseInt(serviceIdMatch[1]);
          const service = servicesList.find(s => s.id === serviceId);

          if (service) {
            // Get edited value from localStorage
            const savedName = getContent(key, service.name);

            // Sync to D1 immediately
            try {
              await api.updateHomeServiceList(serviceId, {
                name: savedName,
                order_num: service.order_num
              });
              console.log('✅ Synced home service list to D1:', serviceId);

              // Reload from D1 to ensure consistency
              const updatedServices = await api.getHomeServiceLists();
              updatedServices.sort((a, b) => a.order_num - b.order_num);
              setServicesList(updatedServices);

              // Clear localStorage
              localStorage.removeItem('homePageContent');
              console.log('🗑️ Cleared localStorage - D1 is now source of truth');
            } catch (error) {
              console.error('❌ Failed to sync home service list to D1:', error);
            }
          }
        }
      }
    };

    window.addEventListener('contentUpdated', handleServiceUpdate);
    return () => window.removeEventListener('contentUpdated', handleServiceUpdate);
  }, [servicesList, getContent]);

  // Add new home service list item (D1 API)
  const handleAddService = async () => {
    try {
      const maxOrderNum = servicesList.length > 0
        ? Math.max(...servicesList.map(s => s.order_num))
        : 0;

      const newServiceData = {
        name: "New Service",
        order_num: maxOrderNum + 1
      };

      const result = await api.createHomeServiceList(newServiceData);
      console.log('✅ Created new home service list with ID:', result.id);

      // Reload from API
      const updatedServices = await api.getHomeServiceLists();
      updatedServices.sort((a, b) => a.order_num - b.order_num);
      setServicesList(updatedServices);
    } catch (error) {
      console.error('❌ Failed to create home service list:', error);
      alert('Failed to create service. Please try again.');
    }
  };

  // Delete home service list item (D1 API)
  const handleDeleteService = async (service) => {
    if (servicesList.length <= 1) {
      alert('You must have at least one service item.');
      return;
    }
    if (window.confirm('Delete this service?')) {
      try {
        await api.deleteHomeServiceList(service.id);
        console.log('✅ Deleted home service list ID:', service.id);

        // Reload from API
        const updatedServices = await api.getHomeServiceLists();
        updatedServices.sort((a, b) => a.order_num - b.order_num);
        setServicesList(updatedServices);
      } catch (error) {
        console.error('❌ Failed to delete home service list:', error);
        alert('Failed to delete service. Please try again.');
      }
    }
  };

  // Default features for WhyChoose section
  const defaultFeatures = [
    {
      iconName: "Sparkles",
      title: "AI-Enhanced Retouching",
      description: "Advanced algorithms meet human artistry for flawless results"
    },
    {
      iconName: "Palette",
      title: "Color Grading Mastery",
      description: "Professional color correction that brings your vision to life"
    },
    {
      iconName: "Zap",
      title: "24-Hour Turnaround",
      description: "Lightning-fast delivery without compromising quality"
    },
    {
      iconName: "Camera",
      title: "RAW Processing",
      description: "Extract maximum detail and dynamic range from every shot"
    }
  ];

  // Icon map for WhyChoose features
  const iconMap = {
    Sparkles: <Sparkles className="w-8 h-8" />,
    Palette: <Palette className="w-8 h-8" />,
    Zap: <Zap className="w-8 h-8" />,
    Camera: <Camera className="w-8 h-8" />,
    Clock: <Clock className="w-8 h-8" />,
    Award: <Award className="w-8 h-8" />,
    Target: <Target className="w-8 h-8" />,
    Image: <Image className="w-8 h-8" />,
    ShoppingBag: <ShoppingBag className="w-8 h-8" />,
    Hourglass: <Hourglass className="w-8 h-8" />
  };

  // State for features
  const [features, setFeatures] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(true);

  // Load home whychoose features from D1 API (only)
  useEffect(() => {
    async function loadHomeWhyChooseFeatures() {
      try {
        setLoadingFeatures(true);
        console.log('📡 Loading home whychoose features from D1 API...');

        const apiFeatures = await api.getHomeWhyChooseFeatures();

        if (apiFeatures && apiFeatures.length > 0) {
          // Transform to match component format (iconName instead of icon_name)
          const transformedFeatures = apiFeatures.map((feature) => ({
            id: feature.id,
            iconName: feature.icon_name,
            title: feature.title,
            description: feature.description || '',
            order_num: feature.order_num || 0
          }));

          // Sort by order_num
          transformedFeatures.sort((a, b) => a.order_num - b.order_num);

          setFeatures(transformedFeatures);
          console.log('✅ Loaded', transformedFeatures.length, 'home whychoose features from D1 API');
        } else {
          console.warn('⚠️ No home whychoose features found in D1 database');
          setFeatures([]);
        }
      } catch (error) {
        console.error('❌ Failed to load home whychoose features from D1 API:', error);
        setFeatures([]);
      } finally {
        setLoadingFeatures(false);
      }
    }

    loadHomeWhyChooseFeatures();
  }, []);

  // Listen for feature edits and sync to D1
  useEffect(() => {
    const handleFeatureUpdate = async (event) => {
      const { key, page } = event.detail || {};

      // Only handle whychoose feature updates
      if (page === 'home' && key && key.startsWith('home_whychoose_feature_')) {
        console.log('📝 WhyChoose feature edit detected:', key);

        // Extract feature ID from key (e.g., home_whychoose_feature_1_title)
        const featureIdMatch = key.match(/home_whychoose_feature_(\d+)_/);
        if (featureIdMatch) {
          const featureId = parseInt(featureIdMatch[1]);
          const feature = features.find(f => f.id === featureId);

          if (feature) {
            // Get edited values from localStorage
            const savedTitle = getContent(`home_whychoose_feature_${featureId}_title`, feature.title);
            const savedDesc = getContent(`home_whychoose_feature_${featureId}_description`, feature.description);

            // Sync to D1 immediately
            try {
              await api.updateHomeWhyChooseFeature(featureId, {
                icon_name: feature.iconName,
                title: savedTitle,
                description: savedDesc,
                order_num: feature.order_num
              });
              console.log('✅ Synced home whychoose feature to D1:', featureId);

              // Reload from D1 to ensure consistency
              const updatedFeatures = await api.getHomeWhyChooseFeatures();
              const transformedFeatures = updatedFeatures.map((f) => ({
                id: f.id,
                iconName: f.icon_name,
                title: f.title,
                description: f.description || '',
                order_num: f.order_num || 0
              }));
              transformedFeatures.sort((a, b) => a.order_num - b.order_num);
              setFeatures(transformedFeatures);

              // Clear localStorage
              localStorage.removeItem('homePageContent');
              console.log('🗑️ Cleared localStorage - D1 is now source of truth');
            } catch (error) {
              console.error('❌ Failed to sync home whychoose feature to D1:', error);
            }
          }
        }
      }
    };

    window.addEventListener('contentUpdated', handleFeatureUpdate);
    return () => window.removeEventListener('contentUpdated', handleFeatureUpdate);
  }, [features, getContent]);

  // Add new feature card (D1 API)
  const handleAddFeature = async () => {
    try {
      const maxOrderNum = features.length > 0
        ? Math.max(...features.map(f => f.order_num))
        : 0;

      const newFeatureData = {
        icon_name: "Sparkles",
        title: "New Feature",
        description: "Click to edit this feature description",
        order_num: maxOrderNum + 1
      };

      const result = await api.createHomeWhyChooseFeature(newFeatureData);
      console.log('✅ Created new home whychoose feature with ID:', result.id);

      // Reload from API
      const updatedFeatures = await api.getHomeWhyChooseFeatures();
      const transformedFeatures = updatedFeatures.map((f) => ({
        id: f.id,
        iconName: f.icon_name,
        title: f.title,
        description: f.description || '',
        order_num: f.order_num || 0
      }));
      transformedFeatures.sort((a, b) => a.order_num - b.order_num);
      setFeatures(transformedFeatures);
    } catch (error) {
      console.error('❌ Failed to create home whychoose feature:', error);
      alert('Failed to create feature. Please try again.');
    }
  };

  // Delete feature card (D1 API)
  const handleDeleteFeature = async (feature) => {
    if (features.length <= 1) {
      alert('You must have at least one feature card.');
      return;
    }
    if (window.confirm('Delete this feature card?')) {
      try {
        await api.deleteHomeWhyChooseFeature(feature.id);
        console.log('✅ Deleted home whychoose feature ID:', feature.id);

        // Reload from API
        const updatedFeatures = await api.getHomeWhyChooseFeatures();
        const transformedFeatures = updatedFeatures.map((f) => ({
          id: f.id,
          iconName: f.icon_name,
          title: f.title,
          description: f.description || '',
          order_num: f.order_num || 0
        }));
        transformedFeatures.sort((a, b) => a.order_num - b.order_num);
        setFeatures(transformedFeatures);
      } catch (error) {
        console.error('❌ Failed to delete home whychoose feature:', error);
        alert('Failed to delete feature. Please try again.');
      }
    }
  };

  // Handle icon change (D1 API)
  const handleIconChange = async (feature, newIconName) => {
    try {
      await api.updateHomeWhyChooseFeature(feature.id, {
        icon_name: newIconName,
        title: feature.title,
        description: feature.description,
        order_num: feature.order_num
      });
      console.log('✅ Updated icon for feature ID:', feature.id);

      // Reload from API
      const updatedFeatures = await api.getHomeWhyChooseFeatures();
      const transformedFeatures = updatedFeatures.map((f) => ({
        id: f.id,
        iconName: f.icon_name,
        title: f.title,
        description: f.description || '',
        order_num: f.order_num || 0
      }));
      transformedFeatures.sort((a, b) => a.order_num - b.order_num);
      setFeatures(transformedFeatures);
    } catch (error) {
      console.error('❌ Failed to update feature icon:', error);
    }
  };

  // State for progress stats
  const [progressStats, setProgressStats] = useState({
    photos_number: '12,720,744',
    orders_number: '309,353',
    hours_number: '424,025',
    main_number: '191,374'
  });
  const [loadingProgressStats, setLoadingProgressStats] = useState(true);

  // Load progress stats from D1 API (only)
  useEffect(() => {
    async function loadProgressStats() {
      try {
        setLoadingProgressStats(true);
        console.log('📡 Loading progress stats from D1 API...');

        const stats = await api.getHomeProgressStats();
        console.log('📊 Raw stats from API:', stats);
        setProgressStats(stats);
        console.log('✅ Loaded progress stats from D1 API:', stats);
      } catch (error) {
        console.error('❌ Failed to load progress stats from D1 API:', error);
      } finally {
        setLoadingProgressStats(false);
      }
    }

    loadProgressStats();
  }, []);

  // Listen for progress stats edits and sync to D1
  useEffect(() => {
    const handleStatsUpdate = async (event) => {
      const { key, page } = event.detail || {};

      // Only handle progress stats updates
      if (page === 'home' && key && key.startsWith('home_stats_')) {
        console.log('📝 Progress stats edit detected:', key);

        // Get edited values from localStorage
        const photosNumber = getContent('home_stats_photos_number', progressStats.photos_number);
        const ordersNumber = getContent('home_stats_orders_number', progressStats.orders_number);
        const hoursNumber = getContent('home_stats_hours_number', progressStats.hours_number);
        const mainNumber = getContent('home_stats_main_number', progressStats.main_number);

        // Sync to D1 immediately
        try {
          await api.updateHomeProgressStats({
            photos_number: photosNumber,
            orders_number: ordersNumber,
            hours_number: hoursNumber,
            main_number: mainNumber
          });
          console.log('✅ Synced progress stats to D1');

          // Reload from D1 to ensure consistency
          const updatedStats = await api.getHomeProgressStats();
          setProgressStats(updatedStats);

          // Clear localStorage
          localStorage.removeItem('homePageContent');
          console.log('🗑️ Cleared localStorage - D1 is now source of truth');
        } catch (error) {
          console.error('❌ Failed to sync progress stats to D1:', error);
        }
      }
    };

    window.addEventListener('contentUpdated', handleStatsUpdate);
    return () => window.removeEventListener('contentUpdated', handleStatsUpdate);
  }, [progressStats, getContent]);

  // Default testimonials data
  const defaultTestimonials = [
    {
      name: "Seth",
      orders: 66,
      editor: "Thu Nguyen",
      editorIcon: "Camera",
      editorColor: "blue",
      category: "",
      review: "I've been using their editing services for 4 months now and couldn't be happier. Great communication, and if there is ever an issue (which is not often) it's taken care of! 10/10 recommend"
    },
    {
      name: "Josh",
      orders: 25,
      editor: "Vu Tuyet",
      editorIcon: "Palette",
      editorColor: "purple",
      category: "",
      review: "Such high-quality edits every single time. Works really well with changes that need to be made. Cannot recommend them enough!"
    },
    {
      name: "Andy",
      orders: 2,
      editor: "markupsolutions.in",
      editorIcon: "Sparkles",
      editorColor: "red",
      category: "",
      review: "Excellent work and super fast turn around! Highly recommended."
    },
    {
      name: "Yohan",
      orders: 1,
      editor: "REPP Editing",
      editorIcon: "Zap",
      editorColor: "cyan",
      category: "Property Video",
      review: "Great turn around time and quality of edits. I had very little expectation for this clip. I provided all the footage and the REPP team could get the best shots and put together in a nice and engaging video. I can't imaging what else they can do with better quality RAW footage. 100% recommended!"
    },
    {
      name: "Sarah",
      orders: 42,
      editor: "Maria Santos",
      editorIcon: "Camera",
      editorColor: "green",
      category: "Real Estate",
      review: "Outstanding service! The HDR edits make my real estate photos look incredibly professional. Fast turnaround and attention to detail is unmatched. Highly recommend for any real estate photographer."
    },
    {
      name: "Michael",
      orders: 18,
      editor: "Alex Kim",
      editorIcon: "Sparkles",
      editorColor: "orange",
      category: "",
      review: "The virtual staging service is a game changer! My listings get more views and the quality is phenomenal. The team is responsive and delivers exactly what I need."
    },
    {
      name: "Jessica",
      orders: 31,
      editor: "David Chen",
      editorIcon: "Palette",
      editorColor: "pink",
      category: "Architecture",
      review: "Consistently excellent results on my architecture projects. The color grading and detail enhancement really brings out the best in my work. Communication is always clear and professional."
    },
    {
      name: "Robert",
      orders: 9,
      editor: "Lisa Wong",
      editorIcon: "Camera",
      editorColor: "indigo",
      category: "",
      review: "Best editing service I've used. The batch processing feature saves me hours of work. Quality is consistent across all images and pricing is very competitive."
    },
    {
      name: "Emma",
      orders: 54,
      editor: "James Miller",
      editorIcon: "Zap",
      editorColor: "teal",
      category: "Product Photography",
      review: "Amazing work on my e-commerce product photos! The background removal and color correction is flawless. My conversion rates have improved significantly. Will definitely continue using their services!"
    }
  ];

  // Icon mapping for testimonials
  const testimonialIconMap = {
    Camera: Camera,
    Sparkles: Sparkles,
    Palette: Palette,
    Zap: Zap
  };

  // State for testimonials
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  // Load testimonials from D1 API (only)
  useEffect(() => {
    async function loadTestimonials() {
      try {
        setLoadingTestimonials(true);
        console.log('📡 Loading testimonials from D1 API...');

        const apiTestimonials = await api.getHomeTestimonials();

        if (apiTestimonials && apiTestimonials.length > 0) {
          // Transform DB fields (snake_case) to component format (camelCase)
          const transformedTestimonials = apiTestimonials.map((t) => ({
            id: t.id,
            name: t.name,
            orders: t.orders || 0,
            editor: t.editor || '',
            editorIcon: t.editor_icon || 'Camera',
            editorColor: t.editor_color || 'blue',
            category: t.category || '',
            review: t.review,
            rating: t.rating || 5,
            order_num: t.order_num || 0
          }));

          transformedTestimonials.sort((a, b) => a.order_num - b.order_num);
          setTestimonials(transformedTestimonials);
          console.log('✅ Loaded', transformedTestimonials.length, 'testimonials from D1 API');
        } else {
          console.warn('⚠️ No testimonials found in D1 database');
          setTestimonials([]);
        }
      } catch (error) {
        console.error('❌ Failed to load testimonials from D1 API:', error);
        setTestimonials([]);
      } finally {
        setLoadingTestimonials(false);
      }
    }

    loadTestimonials();
  }, []);

  // Listen for testimonials updates and sync to D1
  useEffect(() => {
    const handleTestimonialsUpdate = async (event) => {
      const { key, page } = event.detail || {};

      if (page === 'home' && key === 'home_testimonials_data') {
        console.log('📝 Testimonials edit detected, syncing to D1...');

        try {
          // Get updated testimonials from localStorage (temporary buffer)
          const saved = localStorage.getItem('home_testimonials_data');
          if (saved) {
            const updatedTestimonials = JSON.parse(saved);

            // Sync all testimonials to D1
            // Note: This assumes dashboard saves full array
            // We'll need to handle individual updates later
            console.log('🔄 Syncing', updatedTestimonials.length, 'testimonials to D1...');

            // For now, just reload from D1 after any edit
            const apiTestimonials = await api.getHomeTestimonials();
            const transformedTestimonials = apiTestimonials.map((t) => ({
              id: t.id,
              name: t.name,
              orders: t.orders || 0,
              editor: t.editor || '',
              editorIcon: t.editor_icon || 'Camera',
              editorColor: t.editor_color || 'blue',
              category: t.category || '',
              review: t.review,
              rating: t.rating || 5,
              order_num: t.order_num || 0
            }));

            transformedTestimonials.sort((a, b) => a.order_num - b.order_num);
            setTestimonials(transformedTestimonials);

            // Clear localStorage - D1 is now source of truth
            localStorage.removeItem('homePageContent');
            console.log('🗑️ Cleared localStorage - D1 is now source of truth');
          }
        } catch (error) {
          console.error('❌ Failed to sync testimonials to D1:', error);
        }
      }
    };

    window.addEventListener('contentUpdated', handleTestimonialsUpdate);
    return () => window.removeEventListener('contentUpdated', handleTestimonialsUpdate);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {isEditMode && <EditToolbar />}
      <Navigation />

      {/* Header spacing for fixed navbar */}
      <div className="h-12"></div>

      {/* Promotional Offer Bar - Not editable (managed globally) */}
      {promoBarText && (
        <div id="PromoBar" className={`fixed top-12 left-0 right-0 z-40 border-b border-gray-200 transition-transform duration-300 ${scrollY > 100 ? '-translate-y-full' : 'translate-y-0'}`} style={{ backgroundColor: 'rgba(28, 33, 73, 0.7)', backdropFilter: 'blur(10px)' }}>
          <div className="max-w-screen-xl mx-auto px-4 py-2.5 text-center">
            <p className="text-xs md:text-sm text-white font-medium" dangerouslySetInnerHTML={{ __html: promoBarText }} />
          </div>
        </div>
      )}

      {/* Hero Carousel Section */}
      <div id="HomeMain" className="relative min-h-screen overflow-hidden bg-bg-tint-cream pt-[92px]">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            } ${isEditMode && index !== currentSlide ? 'pointer-events-none' : ''}`}
          >
            <div className="absolute inset-0">
              {/* Background Image or Video */}
              {(() => {
                const mediaSrc = getContent(`home_hero_slide_${slide.id}_image`, slide.image);
                const isVideo = isVideoUrl(mediaSrc);

                if (isVideo) {
                  return (
                    <video
                      src={mediaSrc}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  );
                } else {
                  return (
                    <div
                      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                      style={{ backgroundImage: `url(${mediaSrc})` }}
                    />
                  );
                }
              })()}

              {/* Dark Overlay for better text readability */}
              <div className="absolute inset-0 bg-black/40"></div>

              {/* Content */}
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
                <div className="max-w-3xl text-white">
                  <EditableText
                    id={`home_hero_slide_${slide.id}_title`}
                    as="h1"
                    className="text-5xl md:text-7xl font-bold mb-6 leading-tight drop-shadow-lg"
                    defaultValue={slide.title}
                  >
                    {slide.title}
                  </EditableText>
                  <EditableText
                    id={`home_hero_slide_${slide.id}_description`}
                    as="p"
                    className="text-2xl md:text-3xl mb-8 text-white/90 drop-shadow-lg"
                    defaultValue={slide.description}
                  >
                    {slide.description}
                  </EditableText>
                  <div className="flex gap-4">
                    <a
                      href="https://www.mediossolutions.com/signin"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-primary-dark px-8 py-4 rounded-full font-semibold hover:bg-cream-dark transition-all hover:scale-105 inline-flex items-center gap-2"
                    >
                      <EditableText
                        id="home_hero_cta1"
                        as="span"
                        defaultValue="Get Started"
                      >
                        Get Started
                      </EditableText>
                      <ArrowRight className="w-5 h-5" />
                    </a>
                    <a
                      href="tel:+1-917-720-5208"
                      className="bg-white/20 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-full font-semibold hover:bg-white/30 transition-all inline-flex items-center gap-2"
                    >
                      <Phone className="w-5 h-5" />
                      <EditableText
                        id="home_hero_cta2"
                        as="span"
                        defaultValue="Call Us"
                      >
                        Call Us
                      </EditableText>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Carousel Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-50">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
              }`}
              title={`Slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Edit Mode Navigation Arrows */}
        {isEditMode && (
          <div className="absolute top-1/2 -translate-y-1/2 left-4 right-4 flex justify-between pointer-events-none z-50">
            <button
              onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
              className="w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all shadow-lg pointer-events-auto"
              title="Previous slide"
            >
              <ChevronLeft className="w-6 h-6 text-gray-900" />
            </button>
            <button
              onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)}
              className="w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all shadow-lg pointer-events-auto"
              title="Next slide"
            >
              <ChevronRight className="w-6 h-6 text-gray-900" />
            </button>
          </div>
        )}

        {/* Edit Mode Slide Counter */}
        {isEditMode && (
          <div className="absolute top-24 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-full font-semibold text-sm z-50">
            Slide {currentSlide + 1} of {heroSlides.length}
          </div>
        )}

        {/* Edit Mode Controls - Top Right */}
        {isEditMode && (
          <div className="absolute top-24 right-4 flex gap-3 z-50">
            <button
              onClick={() => {
                setCurrentEditingSlideId(heroSlides[currentSlide].id);
                setShowHeroMediaManager(true);
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors font-medium shadow-lg flex items-center gap-2"
              title="Change background image/video"
            >
              <Camera size={16} />
              Change Background
            </button>
            <button
              onClick={handleAddHeroSlide}
              className="px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors font-medium shadow-lg"
              title="Add new hero slide"
            >
              Add Slide
            </button>
            <button
              onClick={() => handleDeleteHeroSlide(heroSlides[currentSlide].id)}
              className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors font-medium shadow-lg"
              title="Delete this slide"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Stats/Progress Section */}
      <div id="progress" className="relative py-32 px-4 bg-bg-tint-blue overflow-hidden">
        {/* 3D Objects Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* 3D Camera Model - Top Right */}
          <div className="absolute top-32 right-32 animate-float-slow opacity-60">
            <div className="relative w-48 h-32 preserve-3d" style={{ transform: 'rotateX(15deg) rotateY(-25deg)' }}>
              {/* Camera Body */}
              <div className="absolute w-40 h-24 bg-gradient-to-br from-gray-300/40 to-gray-400/40 backdrop-blur-sm border border-gray-300/50 rounded-lg shadow-2xl"></div>
              {/* Camera Lens */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-gradient-to-br from-blue-400/50 to-blue-600/50 backdrop-blur-md border-4 border-gray-300/60 shadow-xl">
                <div className="absolute inset-2 rounded-full bg-gradient-to-br from-blue-500/40 to-purple-500/40"></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-600/60 to-black/40"></div>
              </div>
              {/* Camera Flash */}
              <div className="absolute top-2 right-8 w-6 h-4 bg-blue-400/40 backdrop-blur-sm rounded-sm border border-blue-300/50"></div>
              {/* Camera Viewfinder */}
              <div className="absolute top-2 left-8 w-8 h-4 bg-gray-400/30 backdrop-blur-sm rounded-sm border border-gray-300/50"></div>
            </div>
          </div>

          {/* 3D Camera Model - Bottom Left */}
          <div className="absolute bottom-40 left-24 animate-float-medium opacity-50">
            <div className="relative w-40 h-28 preserve-3d" style={{ transform: 'rotateX(-10deg) rotateY(20deg)' }}>
              {/* Camera Body */}
              <div className="absolute w-32 h-20 bg-gradient-to-br from-gray-300/30 to-gray-400/30 backdrop-blur-sm border border-gray-300/40 rounded-lg shadow-xl"></div>
              {/* Camera Lens */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-purple-400/40 to-purple-600/40 backdrop-blur-md border-4 border-gray-300/50">
                <div className="absolute inset-2 rounded-full bg-gradient-to-br from-purple-500/30 to-blue-500/30"></div>
              </div>
            </div>
          </div>

          {/* 3D Camera Icon - Center */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-float-fast opacity-30">
            <div className="relative w-64 h-48 preserve-3d animate-rotate-3d-slow">
              {/* Large Camera Body */}
              <div className="absolute w-56 h-36 bg-gradient-to-br from-blue-300/20 to-purple-300/20 backdrop-blur-sm border border-blue-200/30 rounded-2xl shadow-2xl"></div>
              {/* Large Lens */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full bg-gradient-to-br from-blue-400/30 to-purple-400/30 backdrop-blur-md border-4 border-gray-200/40">
                <div className="absolute inset-3 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20"></div>
                <div className="absolute inset-6 rounded-full bg-gradient-to-br from-blue-600/30 to-black/20"></div>
              </div>
            </div>
          </div>

          {/* Small floating camera elements */}
          <div className="absolute top-48 left-1/3 animate-float-slow-reverse opacity-40">
            <div className="w-20 h-14 bg-gradient-to-br from-blue-400/20 to-purple-400/20 backdrop-blur-sm rounded-lg border border-blue-200/30">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 border-2 border-gray-300/40"></div>
            </div>
          </div>

          <div className="absolute bottom-48 right-1/3 animate-float-medium-reverse opacity-40">
            <div className="w-16 h-12 bg-gradient-to-br from-purple-400/20 to-pink-400/20 backdrop-blur-sm rounded-lg border border-purple-200/30">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/30 border-2 border-gray-300/40"></div>
            </div>
          </div>
        </div>

        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, gray 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          {/* Top Stats Row */}
          <div className="grid md:grid-cols-3 gap-12 mb-20">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 mb-4 rounded-xl bg-orange-100 flex items-center justify-center">
                <Image className="w-8 h-8 text-orange-500" />
              </div>
              <EditableText
                key={`photos-${progressStats.photos_number}`}
                id="home_stats_photos_number"
                as="h3"
                className="text-5xl md:text-6xl font-bold text-gray-900 mb-2"
                defaultValue={progressStats.photos_number}
              >
                {progressStats.photos_number}
              </EditableText>
              <EditableText
                id="home_stats_photos_label"
                as="p"
                className="text-base text-gray-600 font-medium"
                defaultValue="Photos Edited"
              >
                Photos Edited
              </EditableText>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 mb-4 rounded-xl bg-blue-100 flex items-center justify-center">
                <ShoppingBag className="w-8 h-8 text-blue-600" />
              </div>
              <EditableText
                key={`orders-${progressStats.orders_number}`}
                id="home_stats_orders_number"
                as="h3"
                className="text-5xl md:text-6xl font-bold text-gray-900 mb-2"
                defaultValue={progressStats.orders_number}
              >
                {progressStats.orders_number}
              </EditableText>
              <EditableText
                id="home_stats_orders_label"
                as="p"
                className="text-base text-gray-600 font-medium"
                defaultValue="Annual Orders"
              >
                Annual Orders
              </EditableText>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 mb-4 rounded-xl bg-purple-100 flex items-center justify-center">
                <Hourglass className="w-8 h-8 text-purple-600" />
              </div>
              <EditableText
                key={`hours-${progressStats.hours_number}`}
                id="home_stats_hours_number"
                as="h3"
                className="text-5xl md:text-6xl font-bold text-gray-900 mb-2"
                defaultValue={progressStats.hours_number}
              >
                {progressStats.hours_number}
              </EditableText>
              <EditableText
                id="home_stats_hours_label"
                as="p"
                className="text-base text-gray-600 font-medium"
                defaultValue="Est. Hours Saved*"
              >
                Est. Hours Saved*
              </EditableText>
            </div>
          </div>

          {/* Main Message */}
          <div className="mb-12">
            <h2 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <EditableText
                key={`main-${progressStats.main_number}`}
                id="home_stats_main_number"
                as="span"
                className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                defaultValue={progressStats.main_number}
              >
                {progressStats.main_number}
              </EditableText>
              <EditableText
                id="home_stats_main_text"
                as="span"
                className="text-gray-900"
                defaultValue=" photos edited last week."
              >
                {" "}photos edited last week.
              </EditableText>
            </h2>
            <EditableText
              id="home_stats_description"
              as="p"
              className="text-gray-600 text-lg md:text-xl mb-10 max-w-3xl mx-auto"
              defaultValue="Browse hundreds of ready and waiting editors from around the world."
            >
              Browse hundreds of ready and waiting editors from around the world.
            </EditableText>
          </div>

          {/* CTA Button */}
          <div className="flex justify-center">
            <a
              href="https://www.mediossolutions.com/signin"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary-dark transition-all hover:scale-105 shadow-lg"
            >
              <EditableText
                id="home_stats_cta"
                as="span"
                defaultValue="Start for Free"
              >
                Start for Free
              </EditableText>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>

      {/* Feature Gallery Section - SlideGalary */}
      <div id="SlideGalary" className="py-20 bg-bg-tint-beige overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <EditableText
            id="home_slidegalary_title"
            as="h2"
            className="text-4xl md:text-5xl font-bold mb-12 text-center text-gray-900"
            defaultValue="Smile. Snap. Share. Repeat."
          >
            Smile. Snap. Share. Repeat.
          </EditableText>

          {/* Gallery Container */}
          <div className="relative">
            {/* Horizontal Scrolling Gallery */}
            <div ref={slideGalleryRef} className="flex gap-6 overflow-x-hidden pb-8 scroll-smooth">
              {featureGalleryItems.map((item, index) => (
                <div
                  key={item.id}
                  id={`Slide${index + 1}${item.size === 'small' ? 'Portrait' : 'Landscape'}`}
                  className={`relative flex-shrink-0 ${item.size === 'small' ? 'w-[372px]' : 'w-[696px]'} h-[450px] rounded-3xl overflow-hidden cursor-pointer transition-transform hover:scale-105`}
                  onClick={() => !isEditMode && setCurrentFeatureGallery(index)}
                >
                  {/* Edit Mode Controls */}
                  {isEditMode && (
                    <div className="absolute top-2 right-2 z-10 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleSize(item.id);
                        }}
                        className="px-3 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-xs font-medium"
                        title={`Toggle size (currently ${item.size})`}
                      >
                        {item.size === 'large' ? 'Portrait' : 'Landscape'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGalleryItem(item.id);
                        }}
                        className="px-3 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-xs font-medium"
                        title="Delete this item"
                      >
                        Delete
                      </button>
                    </div>
                  )}

                  <EditableMedia
                    id={`home_slidegalary_item_${item.id}`}
                    defaultSrc={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    mediaType="all"
                    autoPlay={true}
                    loop={true}
                    muted={true}
                  />
                </div>
              ))}
            </div>

            {/* Navigation Buttons - Bottom Right */}
            <div className="flex justify-end items-center gap-3 mt-4">
              {isEditMode && (
                <button
                  onClick={handleAddGalleryItem}
                  className="px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  + Add Item
                </button>
              )}
              <button
                onClick={() => navigateSlideGallery('left')}
                className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-all"
                aria-label="Previous feature"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={() => navigateSlideGallery('right')}
                className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-all"
                aria-label="Next feature"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Services List */}
      <div id="ServiceList" className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <EditableText
            id="home_servicelist_heading"
            as="h2"
            className="text-4xl md:text-5xl font-bold mb-4 text-center text-gray-900"
            defaultValue="Full-spectrum editing services"
          >
            Full-spectrum editing services
          </EditableText>

          <EditableText
            id="home_servicelist_description"
            as="p"
            className="text-lg text-gray-600 text-center mb-12"
            defaultValue="Everything you need to make your photos extraordinary"
          >
            Everything you need to make your photos extraordinary
          </EditableText>

          <div className="grid md:grid-cols-2 gap-4">
            {servicesList.map((service, index) => (
              <div
                key={service.id}
                id="ServiceItem"
                className="relative flex items-center gap-4 p-4 bg-white rounded-xl hover:bg-cream transition-all cursor-pointer group"
              >
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <EditableText
                  id={`home_servicelist_item_${service.id}`}
                  as="span"
                  className="text-base font-medium text-gray-900 flex-1"
                  defaultValue={service.name}
                >
                  {service.name}
                </EditableText>

                {/* Delete Button (Edit Mode Only) */}
                {isEditMode && (
                  <button
                    onClick={() => handleDeleteService(service)}
                    className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                    title="Delete service"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add New Service Button (Edit Mode Only) */}
          {isEditMode && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleAddService}
                className="px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors font-medium"
              >
                + Add Service
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Features Grid */}
      <div id="WhyChoose" className="py-20 px-4 relative bg-bg-tint-cream">
        <div className="max-w-7xl mx-auto">
          <EditableText
            id="home_whychoose_heading"
            as="h2"
            className="text-4xl md:text-5xl font-bold mb-4 text-center text-gray-900"
            defaultValue="Why photographers choose us"
          >
            Why photographers choose us
          </EditableText>

          <EditableText
            id="home_whychoose_description"
            as="p"
            className="text-lg text-gray-600 text-center mb-16 max-w-2xl mx-auto"
            defaultValue="Industry-leading editing services trusted by professional photographers worldwide"
          >
            Industry-leading editing services trusted by professional photographers worldwide
          </EditableText>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                id="ChooseCard"
                className="relative p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                {/* Delete Button (Edit Mode Only) */}
                {isEditMode && (
                  <button
                    onClick={() => handleDeleteFeature(feature)}
                    className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors z-10"
                    title="Delete feature card"
                  >
                    <X size={16} />
                  </button>
                )}

                {/* Icon Selection */}
                <div className="mb-4">
                  {isEditMode ? (
                    <div className="flex flex-col gap-2">
                      <div className="text-primary-brown">
                        {iconMap[feature.iconName] || iconMap['Sparkles']}
                      </div>
                      <select
                        value={feature.iconName}
                        onChange={(e) => handleIconChange(feature, e.target.value)}
                        className="text-xs px-2 py-1 border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-discord-500"
                      >
                        {Object.keys(iconMap).map(iconName => (
                          <option key={iconName} value={iconName}>
                            {iconName}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="text-primary-brown">
                      {iconMap[feature.iconName] || iconMap['Sparkles']}
                    </div>
                  )}
                </div>

                {/* Title */}
                <EditableText
                  id={`home_whychoose_feature_${feature.id}_title`}
                  as="h3"
                  className="text-xl font-semibold mb-2 text-gray-900"
                  defaultValue={feature.title}
                >
                  {feature.title}
                </EditableText>

                {/* Description */}
                <EditableText
                  id={`home_whychoose_feature_${feature.id}_description`}
                  as="p"
                  className="text-gray-600 text-sm leading-relaxed"
                  defaultValue={feature.description}
                >
                  {feature.description}
                </EditableText>
              </div>
            ))}
          </div>

          {/* Add New Feature Button (Edit Mode Only) */}
          {isEditMode && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleAddFeature}
                className="px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors font-medium"
              >
                + Add Feature Card
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Testimonials Section */}
      <div id="Testimonials" className="py-20 px-4 bg-bg-tint-beige">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              What your peers are saying about our editors
            </h2>
            <p className="text-lg text-gray-600">
              Join 2,000+ satisfied Medios customers
            </p>
          </div>

          {/* Testimonials Horizontal Scroll */}
          <div className="relative overflow-hidden">
            <div ref={testimonialScrollRef} className="flex gap-6" style={{ willChange: 'transform' }}>
              {/* Render testimonials twice for seamless infinite loop */}
              {[...testimonials, ...testimonials].map((testimonial, index) => {
                const Icon = testimonialIconMap[testimonial.editorIcon] || Camera;
                return (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-80">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{testimonial.name}</h3>
                      <p className="text-sm text-gray-500 mb-2">{testimonial.orders} orders</p>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs text-gray-500">Edits by</span>
                        <div className="flex items-center gap-1">
                          <div className={`w-5 h-5 rounded-full bg-${testimonial.editorColor}-100 flex items-center justify-center`}>
                            <Icon className={`w-3 h-3 text-${testimonial.editorColor}-600`} />
                          </div>
                          <span className="text-xs font-medium text-gray-700">{testimonial.editor}</span>
                        </div>
                      </div>
                    </div>
                    {testimonial.category && testimonial.category.trim() !== "" && (
                      <div className="mb-2">
                        <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">{testimonial.category}</span>
                      </div>
                    )}
                    <div className="flex gap-1 mb-3">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {testimonial.review}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center rounded-2xl p-12" style={{ backgroundColor: '#d4c4b0' }}>
            <EditableText
              id="home_cta_title"
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4 text-gray-900"
              defaultValue="Ready to Transform Your Visual Content?"
            >
              Ready to Transform Your Visual Content?
            </EditableText>
            <EditableText
              id="home_cta_description"
              as="p"
              className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto"
              defaultValue="Let's work together to bring your vision to life. Contact us today for a free consultation."
            >
              Let's work together to bring your vision to life. Contact us today for a free consultation.
            </EditableText>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+1-917-720-5208"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full font-medium hover:bg-gray-100 transition-all hover:scale-105"
              >
                <Phone className="w-5 h-5" />
                <EditableText
                  id="home_cta_phone"
                  as="span"
                  defaultValue="Call Us Now"
                >
                  Call Us Now
                </EditableText>
              </a>
              <a
                href="mailto:info@mediossolutions.com"
                className="inline-flex items-center justify-center gap-2 bg-discord-500 text-white px-8 py-4 rounded-full font-medium hover:bg-discord-500 transition-all hover:scale-105"
              >
                <Mail className="w-5 h-5" />
                <EditableText
                  id="home_cta_email"
                  as="span"
                  defaultValue="Email Us"
                >
                  Email Us
                </EditableText>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Background Image/Video MediaManager */}
      {isEditMode && (
        <MediaPicker
          isOpen={showHeroMediaManager}
          onClose={() => {
            setShowHeroMediaManager(false);
            setCurrentEditingSlideId(null);
          }}
          onSelect={async (url) => {
            if (currentEditingSlideId) {
              console.log('✅ Hero slide background changed to:', url);

              // Update localStorage
              updateContent(`home_hero_slide_${currentEditingSlideId}_image`, url);

              // Update the slides state
              const updatedSlides = heroSlides.map(s =>
                s.id === currentEditingSlideId ? { ...s, image: url } : s
              );
              setHeroSlides(updatedSlides);

              // Sync to D1 API immediately
              try {
                await api.updateHeroSlide(currentEditingSlideId, {
                  media_url: url,
                  media_type: url.includes('.mp4') || url.includes('video') ? 'video' : 'image'
                });
                console.log('✅ Synced hero slide background to D1');
              } catch (error) {
                console.error('❌ Failed to sync to D1:', error);
              }
            }
            setShowHeroMediaManager(false);
            setCurrentEditingSlideId(null);
          }}
          currentUrl={currentEditingSlideId ? getContent(`home_hero_slide_${currentEditingSlideId}_image`, heroSlides.find(s => s.id === currentEditingSlideId)?.image) : ''}
          mediaType="all"
        />
      )}

      <Footer />
    </div>
  );
}
